import { normalizePath } from '../services/analyticsService';
import { getStoredUserSync } from './authStorage';

const STORAGE_PREFIX = 'resto360_recent_pages_';
const MAX_RECENT = 8;
const UPDATE_EVENT = 'recent-pages-updated';

const SKIP_EXACT = new Set([
  '/',
  '/dashboard',
  '/owner/dashboard',
  '/manager/dashboard',
  '/waiter/dashboard',
  '/signin',
  '/signup',
  '/forgot-password',
  '/verify-email',
  '/blocked',
]);

const SKIP_PREFIX = [
  '/qr-menu',
  '/customer-display',
  '/reset-password',
  '/tablet-boundary-editor',
  '/pricing',
  '/terms',
  '/policy',
];

const ROUTE_META = [
  { pattern: /^\/pos(?:\/|$)/, labelKey: 'nav.posSystem', icon: 'cashRegister' },
  { pattern: /^\/branches\/:id\/menu/, labelKey: 'menu.title', icon: 'bookOpen' },
  { pattern: /^\/restaurants\/:id\/menu/, labelKey: 'menu.title', icon: 'bookOpen' },
  { pattern: /^\/branches\/:id\/tables/, labelKey: 'table.tables', icon: 'chair' },
  { pattern: /^\/branches\/:id\/analytics/, labelKey: 'dashboard.reports', icon: 'chartLine' },
  { pattern: /^\/branches\/:id\/sales-report/, labelKey: 'dashboard.reports', icon: 'chartLine' },
  { pattern: /^\/branches\/:id\/expenses/, labelKey: 'nav.expenses', icon: 'receipt' },
  { pattern: /^\/branches\/:id\/settings/, labelKey: 'common.settings', icon: 'cog' },
  { pattern: /^\/branches\/:id\/manager-logs/, labelKey: 'dashboard.reports', icon: 'clipboardList' },
  { pattern: /^\/branches\/:id\/edit/, labelKey: 'nav.branches', icon: 'store' },
  { pattern: /^\/branches\/:id$/, labelKey: 'nav.branches', icon: 'store' },
  { pattern: /^\/branches$/, labelKey: 'nav.branches', icon: 'store' },
  { pattern: /^\/restaurants\/:id\/analytics/, labelKey: 'dashboard.reports', icon: 'chartLine' },
  { pattern: /^\/restaurants\/:id\/branches/, labelKey: 'dashboard.branches', icon: 'store' },
  { pattern: /^\/restaurants\/:id\/edit/, labelKey: 'dashboard.restaurants', icon: 'store' },
  { pattern: /^\/restaurants\/:id$/, labelKey: 'dashboard.restaurants', icon: 'store' },
  { pattern: /^\/restaurants\/new/, labelKey: 'dashboard.addRestaurant', icon: 'plus' },
  { pattern: /^\/restaurants$/, labelKey: 'dashboard.restaurants', icon: 'store' },
  { pattern: /^\/manage-restaurant/, labelKey: 'nav.manageRestaurant', icon: 'utensils' },
  { pattern: /^\/users/, labelKey: 'nav.staff', icon: 'users' },
  { pattern: /^\/orders-history/, labelKey: 'nav.ordersHistory', icon: 'clipboardList' },
  { pattern: /^\/my-orders/, labelKey: 'nav.myOrders', icon: 'clipboardList' },
  { pattern: /^\/expenses/, labelKey: 'nav.expenses', icon: 'receipt' },
  { pattern: /^\/invoices/, labelKey: 'nav.invoices', icon: 'fileInvoice' },
  { pattern: /^\/qr-menu/, labelKey: 'nav.qrMenu', icon: 'qrcode' },
  { pattern: /^\/assigned-menus/, labelKey: 'nav.branchMenus', icon: 'clipboardList' },
  { pattern: /^\/assigned-tables/, labelKey: 'nav.branchTables', icon: 'chair' },
  { pattern: /^\/settings\/themes/, labelKey: 'nav.colorThemes', icon: 'palette' },
  { pattern: /^\/settings/, labelKey: 'common.settings', icon: 'cog' },
  { pattern: /^\/downloads/, labelKey: 'nav.downloads', icon: 'download' },
  { pattern: /^\/admin/, labelKey: 'nav.adminPortal', icon: 'shield' },
  { pattern: /^\/menu/, labelKey: 'menu.title', icon: 'bookOpen' },
  { pattern: /^\/tables/, labelKey: 'table.tables', icon: 'chair' },
  { pattern: /^\/reports/, labelKey: 'dashboard.reports', icon: 'chartLine' },
];

function getUserId() {
  const user = getStoredUserSync();
  return user?._id || user?.id || null;
}

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId}`;
}

function shouldSkip(pathname) {
  const raw = pathname || '/';
  if (SKIP_EXACT.has(raw)) return true;
  if (SKIP_PREFIX.some((prefix) => raw.startsWith(prefix))) return true;
  if (raw.includes('/embed/')) return true;
  return false;
}

export function getRouteMeta(normalizedPath) {
  const path = normalizedPath || '/';
  const match = ROUTE_META.find((entry) => entry.pattern.test(path));
  if (match) return match;
  return { labelKey: null, icon: 'externalLink', fallbackLabel: path };
}

export function getRecentPages(userId) {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

export function trackRecentPage(pathname) {
  try {
    const userId = getUserId();
    if (!userId) return;

    const path = pathname || '/';
    if (shouldSkip(path)) return;

    const normalized = normalizePath(path);
    const existing = getRecentPages(userId).filter((item) => item.path !== path);
    const updated = [
      { path, normalized, visitedAt: Date.now() },
      ...existing,
    ].slice(0, MAX_RECENT);

    localStorage.setItem(storageKey(userId), JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT));
  } catch {
    // Never throw
  }
}

export const RECENT_PAGES_UPDATE_EVENT = UPDATE_EVENT;
